import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  CertificationCase,
  declineCertification,
  getCertificationCase,
  submitCertification,
} from '../../api/certifications';
import { formatDay } from './CertificationQueuePage';
import { BlockSkeleton } from './CertificationSkeleton';
import './CertificationPage.css';

// Signed URLs carry the file name in the path. A photo gets an <img>, because a
// browser shows one in an iframe at its full size, cut off.
const isImage = (url: string): boolean => {
  try {
    return /\.(jpe?g|png|webp|gif|heic)$/i.test(new URL(url, window.location.href).pathname);
  } catch {
    return false;
  }
};

// One POA, in the order the certifier works: look, register the act on the
// Ordem's portal with the summary we prepared, note its numbers, upload the
// page the Ordem generated. Declining is the way out when something does not
// match.
const CertificationCasePage: React.FC = () => {
  const { caseId = '' } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [details, setDetails] = useState<CertificationCase | null>(null);
  const [error, setError] = useState('');
  const [registration, setRegistration] = useState('');
  const [consultation, setConsultation] = useState('');
  const [signedFile, setSignedFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<'' | 'certify' | 'decline'>('');
  const [copied, setCopied] = useState('');
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  const [actionError, setActionError] = useState('');
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [shown, setShown] = useState(0);
  const [enlarged, setEnlarged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const data = await getCertificationCase(token, caseId);
        if (!cancelled) setDetails(data);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load this case.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser, caseId]);

  useEffect(() => {
    if (!enlarged) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEnlarged(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enlarged]);

  const numbers = { registration_number: registration.trim(), consultation_code: consultation.trim() };
  const numbersReady = Boolean(numbers.registration_number && numbers.consultation_code);

  const run = async (kind: 'certify' | 'decline', action: (token: string) => Promise<void>) => {
    if (!currentUser) return;
    setBusy(kind);
    setActionError('');
    try {
      await action(await currentUser.getIdToken());
    } catch (err: any) {
      setActionError(err.message || 'Something went wrong.');
    } finally {
      setBusy('');
    }
  };

  // `key` names what was copied, so only its button says "Copied".
  const copy = async (key: string, text: string, field: HTMLInputElement | HTMLTextAreaElement | null) => {
    setActionError('');
    let done = false;
    try {
      await navigator.clipboard.writeText(text);
      done = true;
    } catch {
      // Refused when the browser denies the permission or the page has lost
      // focus. Copying the selected text still works there.
      field?.focus();
      field?.select();
      try {
        done = document.execCommand('copy');
      } catch {
        done = false;
      }
    }
    if (done) {
      setCopied(key);
      setTimeout(() => setCopied(current => (current === key ? '' : current)), 3000);
    } else {
      setActionError('Copying did not work. The text is selected: press Ctrl+C or Cmd+C.');
    }
  };

  const handleCopy = () => details && copy('summary', details.summary, summaryRef.current);

  const handleCertify = () =>
    run('certify', async token => {
      if (!signedFile) return;
      await submitCertification(token, caseId, numbers, signedFile);
      navigate('/certifications', { replace: true });
    });

  const handleDecline = () =>
    run('decline', async token => {
      await declineCertification(token, caseId, reason.trim());
      navigate('/certifications', { replace: true });
    });

  if (error) {
    return (
      <div className="certification-page">
        <Link to="/certifications">← Back to the queue</Link>
        <div className="certification-error" role="alert">{error}</div>
      </div>
    );
  }
  if (!details) return <div className="certification-page"><BlockSkeleton lines={8} /></div>;

  // Backend order: the POA first, so that is what opens.
  const shownDocument = details.documents[shown];
  const others = details.documents.filter(d => d.kind !== 'poa');

  return (
    <div className="certification-page">
      <Link to="/certifications">← Back to the queue</Link>
      <h1>{details.name || 'Unnamed'}</h1>
      <p className="certification-intro">
        {details.minor ? "A child's case: the POA is signed by the parents. " : ''}
        {details.hand_signed
          ? `POA signed by hand on ${formatDay(details.poa_signed_on)}: a scan, with no audit log.`
          : `POA signed electronically on ${formatDay(details.poa_signed_on)}.`}
      </p>

      <div className="certification-layout">
        <section className="certification-viewer" aria-label="Signed power of attorney">
          <div className={`certification-viewer-frame${enlarged ? ' certification-viewer-frame--large' : ''}`}>
            <div className="certification-docs">
              {details.documents.map((doc, index) => (
                <button
                  key={`${doc.kind}-${index}`}
                  className={`certification-doc${index === shown ? ' certification-doc--active' : ''}`}
                  aria-pressed={index === shown}
                  onClick={() => setShown(index)}
                >
                  {doc.label}
                </button>
              ))}
              <button className="certification-btn certification-btn--link" onClick={() => setEnlarged(!enlarged)}>
                {enlarged ? 'Close' : 'Enlarge'}
              </button>
            </div>
            {shownDocument && (isImage(shownDocument.url) ? (
              <div className="certification-image"><img src={shownDocument.url} alt={shownDocument.label} /></div>
            ) : (
              <iframe key={shownDocument.url} title={shownDocument.label} src={shownDocument.url} />
            ))}
          </div>
          {others.length === 0 && <p className="certification-note">No audit log or identity document on this case.</p>}
        </section>

        <section className="certification-steps" aria-label="Certify">
          <ol>
            <li>
              <h2>Check</h2>
              <p>
                The name on the POA matches the identity document
                {details.hand_signed ? ', and the POA carries a handwritten signature.' : ', and the signing audit log is there.'}
              </p>
            </li>
            <li>
              <h2>Register with the Ordem</h2>
              <p>
                On the Ordem dos Advogados portal, register the act ("Certificação de fotocópias"), add who signed under
                "Interessado(s)" and paste the text below into "Resumo do ato". Then sign the page the Ordem generates and
                download it.
              </p>
              {(details.interested_parties || []).length > 0 && (
                <div className="certification-parties" aria-label="Interessados">
                  <p>For "Interessado(s)":</p>
                  {(details.interested_parties || []).map((party, index) => (
                    <div className="certification-party" key={index}>
                      <span className="certification-party-type">{party.document || 'Document type unknown'}</span>
                      {(['number', 'name'] as const).map(field => {
                        const value = party[field] || '';
                        const key = `${field}-${index}`;
                        const label = field === 'number' ? 'Number' : 'Name';
                        return (
                          <label key={field}>
                            {label}
                            <span className="certification-copy-field">
                              <input
                                id={key}
                                value={value}
                                readOnly
                                placeholder="Not on the case"
                                onFocus={e => e.target.select()}
                              />
                              <button
                                type="button"
                                className="certification-btn certification-btn--small"
                                disabled={!value}
                                aria-label={`Copy ${label.toLowerCase()}`}
                                onClick={() => copy(key, value, document.getElementById(key) as HTMLInputElement | null)}
                              >
                                {copied === key ? 'Copied' : 'Copy'}
                              </button>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              {details.details_missing && (
                <div className="certification-error" role="alert">
                  Your cédula or office address is missing from this text. Add them under{' '}
                  <Link to="/certifications/me">My details</Link> first.
                </div>
              )}
              <textarea
                ref={summaryRef}
                className="certification-summary"
                aria-label="Summary for the Ordem"
                value={details.summary}
                readOnly
                rows={9}
                onFocus={e => e.target.select()}
              />
              <button className="certification-btn" onClick={handleCopy}>
                {copied === 'summary' ? 'Copied' : 'Copy text'}
              </button>
            </li>
            <li>
              <h2>Enter the Ordem's numbers</h2>
              <p>Both are on the page the Ordem generated.</p>
              <label>
                Registration number
                <input value={registration} onChange={e => setRegistration(e.target.value)} placeholder="12345L/A000198" />
              </label>
              <label>
                Consultation code
                <input value={consultation} onChange={e => setConsultation(e.target.value)} placeholder="57364526-889613" />
              </label>
            </li>
            <li>
              <h2>Upload the Ordem's page</h2>
              <p>The PDF you signed on the Ordem portal.</p>
              <input
                type="file"
                accept="application/pdf"
                aria-label="Certification from the Ordem"
                onChange={e => setSignedFile(e.target.files?.[0] || null)}
              />
              <button
                className="certification-btn certification-btn--primary"
                onClick={handleCertify}
                disabled={!numbersReady || !signedFile || busy !== ''}
              >
                {busy === 'certify' ? 'Saving…' : 'Certify'}
              </button>
            </li>
          </ol>

          {actionError && <div className="certification-error" role="alert">{actionError}</div>}

          <div className="certification-decline">
            {!declining ? (
              <button className="certification-btn certification-btn--link" onClick={() => setDeclining(true)}>
                Something doesn't match? Decline
              </button>
            ) : (
              <>
                <label>
                  Why you can't certify this POA (the lawyer reads this)
                  <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} />
                </label>
                <button
                  className="certification-btn"
                  onClick={handleDecline}
                  disabled={reason.trim().length < 3 || busy !== ''}
                >
                  {busy === 'decline' ? 'Saving…' : 'Decline'}
                </button>
                <button className="certification-btn certification-btn--link" onClick={() => setDeclining(false)}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CertificationCasePage;
